import { useEffect, useState } from 'react';
import { BodyText } from '@gaiads/telia-react-component-library';
import { AdminUserAssetResponse, ApprovalChainResponse, ApprovalChainStepResponse, ApproverType } from '@cdaas/api-clients/dist/asset-admin-openapi';
import { AdminProductOrder, EndUserType } from '@cdaas/api-clients/dist/order-admin-openapi';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import {
  COUNTRIES,
  OrderDetails as OrderDetailsReusable,
  OrderDetailsMode,
  OrderDetailsProps,
  OrderDetailsTranslations,
} from '@cdaas/reusable-components-daas';

import { assetAdminApprovalChainClient, assetAdminClient, assetAdminUserClient } from '../../api-client/asset-admin-client';
import { orderAdminProductOrderClient } from '../../api-client/order-admin-client';
import { ASSET_MAX_LIMIT } from '../../utils';
import { routes, withCustomerOrgFormPath } from '../App/routes';
import { Loader } from '../App/Loader';
import Message from '../App/Message';

interface OrderDetailsInterface {
  order: AdminProductOrder;
  currentAssets: AdminUserAssetResponse[];
  previousOrders: AdminProductOrder[];
  approvalChain?: ApprovalChainResponse;
}

const OrderDetails = ({ organizationId, order }: { organizationId: string; order?: AdminProductOrder }) => {
  const { t } = useTranslation('customerOrders');
  const { t: tGlobal } = useTranslation('global');

  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState(false);

  const history = useHistory();

  const [orderDetailsData, setOrderDetailsData] = useState<OrderDetailsInterface>();

  useEffect(() => {
    const fetchOrderDetails = async (organizationId: string) => {
      setLoading(true);
      try {
        const userId = order?.packages[0].end_user.user_id;
        if (!userId) {
          setApiError(true);
          setLoading(false);
          return;
        }

        let userGroupId = '';
        try {
          userGroupId = (await assetAdminUserClient.adminUserOfUsersRead(organizationId, userId)).data.user_group.id;
        } catch {
          // User not found, could be deleted after order creation
        }
        const approvalChainId = order.customer_approval.approval_chain_id;

        const responses = await Promise.all([
          // Get user's current assets
          // If user is not found (e.g. deleted) then not needed to fetch user assets
          userGroupId ? assetAdminClient.adminUserAssetsList(organizationId, userGroupId, userId, ASSET_MAX_LIMIT) : Promise.resolve(null),
          // Get user's previous orders
          orderAdminProductOrderClient.listProductOrder(
            undefined,
            undefined,
            undefined,
            undefined,
            [userId],
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            order.created,
          ),
          // Get order's approval chain if order has one
          // If approval chain is not found, e.g. because of manual changes in organization, return null
          approvalChainId
            ? assetAdminApprovalChainClient.adminOrganizationApprovalChainRead(organizationId, approvalChainId).catch(() => null)
            : Promise.resolve(null),
        ]);

        setOrderDetailsData({
          order,
          currentAssets: responses[0]?.data?.contents ?? [],
          previousOrders: responses[1].data.contents,
          approvalChain: responses[2]?.data,
        });
      } catch (error) {
        console.error(error);
        setApiError(true);
      }
      setLoading(false);
    };

    if (order?.packages[0].end_user.type !== EndUserType.External) {
      fetchOrderDetails(organizationId);
    }
  }, [organizationId, order]);

  const translations: OrderDetailsTranslations = {
    orderId: t('orderDetails.orderId'),
    approvalChain: t('orderDetails.approvalChain'),
    price: t('orderDetails.price'),
    leasePeriod: t('orderDetails.leasePeriod'),
    hardware: t('orderDetails.hardware'),
    services: t('orderDetails.services'),
    insurance: t('orderDetails.insurance'),
    insuranceUrl: process.env.REACT_APP_INSURANCE_URI || '',
    user: t('orderDetails.user'),
    purchaser: t('orderDetails.purchaser'),
    delivery: t('orderDetails.delivery'),
    devicesInUse: t('orderDetails.devicesInUse'),
    previousOrders: t('orderDetails.previousOrders'),
    month: t('orderDetails.month'),
    months: t('orderDetails.months'),
    includingVat: t('orderDetails.includingVat'),
    excludingVat: t('orderDetails.excludingVat'),
    orderStatusTranslations: {
      approved: t('orderList.statuses.approved'),
      delivered: t('orderList.statuses.delivered'),
      pendingApproval: t('orderList.statuses.pendingApproval'),
      financingDeclined: t('orderList.statuses.financingDeclined'),
      approvalDeclined: t('orderList.statuses.approvalDeclined'),
      cannotBeProcessed: t('orderList.statuses.cannotBeProcessed'),
      cancelled: t('orderList.statuses.cancelled'),
      returned: t('orderList.statuses.returned'),
      processingOrder: t('orderList.statuses.processingOrder'),
      discarded: t('orderList.statuses.discarded'),
      draft: t('orderList.statuses.draft'),
    },
    approvalStatuses: {
      pending: t('orderDetails.approvalStepStatuses.pending'),
      approved: t('orderDetails.approvalStepStatuses.approved'),
      rejected: t('orderDetails.approvalStepStatuses.rejected'),
    },
    countries: {
      Finland: t('orderDetails.countries.Finland'),
      Norway: t('orderDetails.countries.Norway'),
      Sweden: t('orderDetails.countries.Sweden'),
    },
    orderCancelButton: t('orderDetails.orderCancelButton'),
    orderCancelledTitle: t('orderDetails.orderCancelledTitle'),
    orderCancelledText: t('orderDetails.orderCancelledText'),
    orderCancelErrorTitle: t('orderDetails.orderCancelErrorTitle'),
    orderCancelErrorText: t('orderDetails.orderCancelErrorText'),
    orderRestartButton: t('orderDetails.orderRestartButton'),
    orderRestartedTitle: t('orderDetails.orderRestartedTitle'),
    orderRestartedText: t('orderDetails.orderRestartedText'),
    orderRestartErrorTitle: t('orderDetails.orderRestartErrorTitle'),
    orderRestartErrorText: t('orderDetails.orderRestartErrorText'),
    orderHistoryButton: t('orderDetails.orderHistoryButton'),
    orderHistoryTitle: t('orderDetails.orderHistoryTitle'),
    customerSupportName: t('orderDetails.customerSupportName'),
    companyDevice: tGlobal('companyDevice'),
    approvalStatusTranslations: {
      notNeeded: t('orderDetails.approvalStatuses.notneeded'),
      autoApproved: t('orderDetails.approvalStatuses.autoApproved'),
      pending: t('orderDetails.approvalStatuses.pending'),
      approved: t('orderDetails.approvalStatuses.approved'),
      rejected: t('orderDetails.approvalStatuses.rejected'),
    },
    coltOrderId: t('orderDetails.coltOrderId'),
    coltHardwareDeliveryId: t('orderDetails.coltHardwareDeliveryId'),
  };

  const handleCancelOrderButtonClick = async (): Promise<boolean> => {
    try {
      if (order?.id) {
        await orderAdminProductOrderClient.cancelProductOrder(order.unique_id.toString());
        return true;
      } else {
        return false;
      }
    } catch {
      return false;
    }
  };

  const handleRestartOrderButtonClick = async (): Promise<boolean> => {
    try {
      if (order?.id) {
        await orderAdminProductOrderClient.continueProductOrder(order.unique_id.toString());
        return true;
      } else {
        return false;
      }
    } catch {
      return false;
    }
  };

  /**
   * Add approver user group path to history
   * <p>If user group is not found, root path is used. It is possible that user group is deleted after approval chain is completed.</p>
   * @param userGroupId
   */
  const addApproverUserGroupToHistory = async (userGroupId: string): Promise<void> => {
    try {
      const userGroup = await assetAdminApprovalChainClient.readOrganizationUserGroupApprover(organizationId, userGroupId);
      history.push(`${withCustomerOrgFormPath(routes.userGroups, userGroup.data.organization.id)}/${userGroupId}`);
    } catch {
      history.push(`/`);
    }
  };

  const customApproverElement = (approvalChainStep: ApprovalChainStepResponse): JSX.Element | undefined => {
    switch (approvalChainStep.type) {
      case ApproverType.UserGroup:
        return (
          <BodyText color="purple500" onClick={() => addApproverUserGroupToHistory(approvalChainStep.id)} clickableAppearance={true}>
            {approvalChainStep.name}
          </BodyText>
        );
      case ApproverType.User:
        // Don't customize
        return undefined;
    }
  };

  const approvalChain = (): OrderDetailsProps['approvalChain'] | undefined => {
    if (!orderDetailsData?.approvalChain) {
      return undefined;
    }
    return {
      ...orderDetailsData.approvalChain,
      chain: orderDetailsData.approvalChain.chain.map(approvalChainStep => ({
        ...approvalChainStep,
        customDisplayedApprover: customApproverElement(approvalChainStep),
      })),
    };
  };

  const render = () => {
    if (loading) {
      return <Loader />;
    } else if (apiError) {
      return <Message text={t('orderDetails.fetchError')} />;
    } else {
      return (
        <OrderDetailsReusable
          translations={translations}
          order={order!}
          currentAssets={orderDetailsData?.currentAssets}
          previousOrders={orderDetailsData?.previousOrders}
          approvalChain={approvalChain()}
          countries={COUNTRIES}
          onRestartButtonClick={handleRestartOrderButtonClick}
          onCancelButtonClick={handleCancelOrderButtonClick}
          mode={OrderDetailsMode.Admin}
        />
      );
    }
  };

  return render();
};

export default OrderDetails;
